import {
	OptionRelatedInstituteSettings,
	ProctoringOption,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";
import { ExemptedStudent, VerityInstituteSettings } from "../../types/common";
import { useMobileLayout } from "../../hooks";
import styles from "./ViewDetailsModal.module.css";
import { CoreModal } from "../../core";
import i18nPackage from "../../i18n/i18n";
import { DEFAULT_PROCTORING_CONFIGURATION_OPTIONS, enforceConfiguration } from "@verity/proctoring-option";
import {
	AccommodationDetailsBlock,
	ProfileBlock,
	PlatformBlock,
	ConfigurationBlock,
	AccommodationNotesBlock,
} from "../confirmation/ConfirmationBlock";
import { getPlatformTextFromProctoringOptions } from "../proctoring-options";
import { ProctoringDataType } from "../hooks/state/useSettingsState";
import { useEditQuizStyles } from "../components";
import { ResponsiveModalBody } from "../components/EditQuizModalComponents";

export type ViewDetailsModalProps = {
	onClose: () => void;
	profile?: ProctoringOptionProfileV2;
	exemptedStudent?: ExemptedStudent;
	configuration?: {
		verityInstituteSettings: VerityInstituteSettings;
		optionRelatedInstituteSettings: OptionRelatedInstituteSettings | undefined;
		proctoringData: Pick<ProctoringDataType, "applications" | "extensions">;
		proctoringOption: Partial<ProctoringOption>;
	};
	title: string;
	description?: string;
};

export default function ViewDetailsModal({
	configuration,
	exemptedStudent,
	profile,
	onClose,
	title,
	description,
}: ViewDetailsModalProps) {
	const isMobileLayout = useMobileLayout();
	const { getEditQuizModalWidth, getModalInModalProps } = useEditQuizStyles();

	return (
		<CoreModal
			{...getEditQuizModalWidth(isMobileLayout)}
			{...getModalInModalProps(isMobileLayout, title)}
			heightFixed={false}
			contentMatchModalHeight
			minHeight={undefined}
			onClose={onClose}
			open
			buttons={[
				{
					text: i18nPackage.t("editQuizPage.accommodationStep.closeButton"),
					color: "secondary",
					onClick: onClose,
				},
			]}
		>
			<ResponsiveModalBody contentClassName={styles.viewDetailsModalPage} title={title} description={description}>
				<div className={styles.detailsSectionWrapper}>
					{exemptedStudent && (
						<AccommodationDetailsBlock
							name={
								exemptedStudent.name
									? exemptedStudent.name
									: i18nPackage.t("editQuizPage.accommodationStep.unknownStudent")
							}
							type={exemptedStudent.proctoringOptionProfileId ? "customProfile" : "notProctored"}
							showCheckmark={false}
						/>
					)}
					{profile && <ProfileBlock profile={profile} showCheckmark={false} />}
					{exemptedStudent && (
						<AccommodationNotesBlock showCheckmark={false} notes={exemptedStudent.accommodationNotes} />
					)}
					{configuration && (
						<PlatformBlock
							platformText={getPlatformTextFromProctoringOptions(
								enforceConfiguration(
									DEFAULT_PROCTORING_CONFIGURATION_OPTIONS,
									profile?.proctoringOption ?? {},
									configuration.verityInstituteSettings,
									configuration.optionRelatedInstituteSettings,
								).proctoringOptions,
							)}
							showCheckmark={false}
						/>
					)}
				</div>
				{configuration && (
					<ConfigurationBlock
						verityInstituteSettings={configuration.verityInstituteSettings}
						optionRelatedInstituteSettings={configuration.optionRelatedInstituteSettings}
						proctoringOption={configuration.proctoringOption}
						applications={configuration.proctoringData.applications}
						extensions={configuration.proctoringData.extensions}
						showCheckmark={false}
					/>
				)}
			</ResponsiveModalBody>
		</CoreModal>
	);
}
